import React from "react";
import adminAxios from "../../utils/adminAxios";
import { toast } from "react-toastify";
import { 
  FiX, 
  FiCheckCircle, 
  FiClock, 
  FiAward, 
  FiImage,
  FiSettings,
  FiUser,
  FiCalendar
} from "react-icons/fi";

const RequestDetails = ({ request, open, setOpen, onConfirm }) => {
  if (!open || !request) return null;

  const handleConfirm = async () => {
    try {
      await adminAxios.patch(
        "live-auction-requests/confirm-request",
        null,
        { params: { request_id: request.id } }
      );
      toast.success("تم تأكيد الطلب بنجاح");
      setOpen(false);
      if (onConfirm) onConfirm();
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل في تأكيد الطلب");
    }
  };

  // Calculate time remaining
  const getTimeRemaining = () => {
    const now = new Date();
    const expiry = new Date(request.expiryDate);
    const diff = expiry - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days} يوم`;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return `${hours} ساعة`;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"
        onClick={() => setOpen(false)}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-4xl transform transition-all">
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 to-purple-700 rounded-t-3xl p-6 text-white">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-2xl font-bold mb-2 text-right">
                  تفاصيل طلب المزاد
                </h2>
                <div className="flex items-center justify-end gap-4 text-blue-100">
                  <div className="flex items-center gap-2">
                    <FiClock className="w-4 h-4" />
                    <span className="text-sm">متبقي: {getTimeRemaining()}</span>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                    request.confirmed 
                      ? 'bg-green-500 text-white' 
                      : 'bg-amber-500 text-white'
                  }`}>
                    {request.confirmed ? (
                      <>
                        <FiCheckCircle className="w-4 h-4" />
                        <span className="text-sm">مفعل</span>
                      </>
                    ) : (
                      <>
                        <FiClock className="w-4 h-4" />
                        <span className="text-sm">قيد الانتظار</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="flex-shrink-0 p-2 hover:bg-white hover:bg-opacity-20 rounded-xl transition-colors"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Image and Basic Info */}
              <div className="space-y-6">
                {/* Auction Image */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-4">
                  <div className="text-center">
                    {request?.image_url ? (
                      <div className="relative group">
                        <img
                          src={process.env.REACT_APP_IMAGE_URL + request.image_url}
                          alt={request.title || "صورة المزاد"}
                          className="w-full h-64 object-cover rounded-xl shadow-lg transition-transform group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-xl transition-all" />
                      </div>
                    ) : (
                      <div className="w-full h-64 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-xl flex flex-col items-center justify-center">
                        <FiImage className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-gray-500 dark:text-gray-400">لا توجد صورة</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Basic Information */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-right flex items-center justify-end gap-2">
                    <FiSettings className="w-5 h-5" />
                    المعلومات الأساسية
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">اسم المزاد</span>
                      <span className="font-semibold text-gray-900 dark:text-white text-right">
                        {request?.title || "غير متوفر"}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">صاحب المزاد</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {request?.user?.name || "غير متوفر"}
                        </span>
                        <FiUser className="w-4 h-4 text-blue-500" />
                      </div>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">بداية المزاد</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {new Date(request?.startDate).toLocaleDateString('ar-EG')}
                        </span>
                        <FiCalendar className="w-4 h-4 text-green-500" />
                      </div>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded-xl">
                      <span className="text-gray-600 dark:text-gray-300">نهاية المزاد</span>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {new Date(request?.expiryDate).toLocaleDateString('ar-EG')}
                        </span>
                        <FiCalendar className="w-4 h-4 text-red-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Options and Actions */}
              <div className="space-y-6">
                {/* Auction Options */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-right flex items-center justify-end gap-2">
                    <FiAward className="w-5 h-5" />
                    خيارات المزاد
                  </h3>

                  {request?.request_products?.length > 0 ? (
                    <div className="space-y-3">
                      {request.request_products.map((option, index) => (
                        <div
                          key={index}
                          className="bg-white dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 transition-colors"
                        >
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="text-right">
                              <span className="text-gray-500 dark:text-gray-400">المنتج:</span>
                              <p className="font-semibold text-gray-900 dark:text-white mt-1">
                                {option.product || "غير متوفر"}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-gray-500 dark:text-gray-400">حد السوم:</span>
                              <p className="font-semibold text-green-600 dark:text-green-400 mt-1">
                                {option.actualPrice ? `${option.actualPrice} ريال` : "غير متوفر"}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-gray-500 dark:text-gray-400">فرق السوم:</span>
                              <p className="font-semibold text-blue-600 dark:text-blue-400 mt-1">
                                {option.bidPrice ? `${option.bidPrice} ريال` : "غير متوفر"}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="text-gray-500 dark:text-gray-400">فتح الباب:</span>
                              <p className="font-semibold text-purple-600 dark:text-purple-400 mt-1">
                                {option.minBidPrice ? `${option.minBidPrice} ريال` : "غير متوفر"}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-white dark:bg-gray-700 rounded-xl">
                      <FiAward className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400">لا توجد خيارات متوفرة</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!request?.confirmed && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-green-200 dark:border-green-800">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 text-right">
                      إجراءات الطلب
                    </h3>
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300 text-right">
                        بعد التأكيد، سيتم تفعيل المزاد وسيتمكن المستخدمون من المشاركة فيه.
                      </p>
                      <button
                        onClick={handleConfirm}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold py-3 px-6 rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                      >
                        <FiCheckCircle className="w-5 h-5" />
                        تأكيد تفعيل المزاد
                      </button>
                    </div>
                  </div>
                )}

                {/* Status Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-800">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 text-right">
                    ملخص الحالة
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">رقم الطلب:</span>
                      <span className="font-mono text-gray-900 dark:text-white">#{request.id}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">تاريخ الإنشاء:</span>
                      <span className="text-gray-900 dark:text-white">
                        {new Date(request.createdAt || new Date()).toLocaleDateString('ar-EG')}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 dark:text-gray-300">عدد الخيارات:</span>
                      <span className="text-gray-900 dark:text-white">
                        {request.request_products?.length || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-b-3xl">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                آخر تحديث: {new Date().toLocaleTimeString('ar-EG')}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setOpen(false)}
                  className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  إغلاق
                </button>
                {!request?.confirmed && (
                  <button
                    onClick={handleConfirm}
                    className="px-6 py-2 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors flex items-center gap-2"
                  >
                    <FiCheckCircle className="w-4 h-4" />
                    تأكيد سريع
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetails;